import { motion } from "framer-motion";

const CHAPTERS = [
    {
        n: "01",
        title: "We do not compete on volume.",
        body: "We have built thirty-seven projects in eighteen years. That is a deliberate number. It lets us stay at every site, every week, until it is done well.",
        image: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?q=80&w=1200&auto=format&fit=crop",
    },
    {
        n: "02",
        title: "We choose materials that age with grace.",
        body: "Hand-cast concrete. Regional laterite and Kadapa stone. Lime plaster. Recycled teak. Materials that look better on the tenth year than on the first.",
        image: "https://images.unsplash.com/photo-1519974719765-e6559eac2575?q=80&w=1200&auto=format&fit=crop",
    },
    {
        n: "03",
        title: "We design for the monsoon, not around it.",
        body: "Roofs that hold rain and release it slowly. Deep loggias. Cross-ventilation. Landscapes that flood beautifully and drain quietly.",
        image: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?q=80&w=1200&auto=format&fit=crop",
    },
    {
        n: "04",
        title: "We hand back keys when the paint is dry.",
        body: "No delivered-under-completion. Every KLiving home is finished — snag list, landscaping, community amenities — before you are asked to move in.",
        image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=1200&auto=format&fit=crop",
    },
];

const EASE = [0.22, 1, 0.36, 1];

export default function Manifesto() {
    return (
        <section
            data-testid="manifesto-section"
            className="relative bg-forest-900 text-ivory py-20 md:py-32 overflow-hidden"
        >
            <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16">
                
                {/* Header Grid Section */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20 md:mb-28 items-end">
                    <div className="md:col-span-6 lg:col-span-5">
                        <div className="flex items-center gap-4 mb-5">
                            <span className="h-px w-8 bg-lime/60" />
                            <span className="font-mono-mini text-[10px] tracking-[0.45em] uppercase text-lime font-medium">
                                Manifesto
                            </span>
                        </div>
                        <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                            Four notes on how we build.
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:col-start-8 text-ivory/60 text-base md:text-lg leading-relaxed font-light">
                        Our practice is written down in a small pocket-book we hand every new
                        engineer, architect and site foreman on their first day. These are the
                        four sentences on the first page.
                    </div>
                </div>

                {/* Chapters List */}
                <ol className="space-y-20 md:space-y-32">
                    {CHAPTERS.map((c, i) => {
                        const isEven = i % 2 === 0;
                        
                        return (
                            <motion.li
                                key={c.n}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-10% 0px" }}
                                transition={{ duration: 0.95, ease: EASE }}
                                data-testid={`manifesto-chapter-${c.n}`}
                                // Changed items-start to items-center for dynamic vertical anchoring
                                className="grid grid-cols-1 md:grid-cols-12 items-center gap-8 md:gap-12 border-t border-white/10 pt-12 md:pt-16"
                            >
                                {/* 1. Number Track (Takes up 2 Columns Always) */}
                                <div className="md:col-span-2 md:order-1 flex md:flex-col items-baseline md:items-start justify-between md:justify-start gap-2">
                                    <div className="font-serif-display text-[5rem] md:text-[7.5rem] leading-[0.8] text-lime/15 italic font-semibold">
                                        {c.n}
                                    </div>
                                    <div className="font-mono-mini text-[9px] tracking-[0.35em] uppercase text-lime/50 md:mt-3">
                                        Chapter
                                    </div>
                                </div>

                                {/* 2. Image Track (Shifted to Landscape ratio and balanced to 5 Columns) */}
                                <motion.div
                                    initial={{ opacity: 0, scale: 1.02 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 1.1, ease: EASE }}
                                    className={`md:col-span-5 ${isEven ? "md:order-3" : "md:order-2"}`}
                                >
                                    {/* Changed aspect-[4/5] to aspect-[3/2] for premium landscape frame */}
                                    <div className="relative overflow-hidden aspect-[3/2] bg-forest-800 rounded-sm shadow-xl shadow-black/10">
                                        <img
                                            src={c.image}
                                            alt={c.title}
                                            loading="lazy"
                                            className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-forest-900/40 via-transparent to-transparent" />
                                        <div className="absolute bottom-3 left-4 font-mono-mini text-[9px] tracking-[0.3em] uppercase text-ivory/70">
                                            Detail · Ch. {c.n}
                                        </div>
                                    </div>
                                </motion.div>

                                {/* 3. Text Content Track (Expanded to 5 Columns for high readability balance) */}
                                <div className={`md:col-span-5 ${isEven ? "md:order-2" : "md:order-3"}`}>
                                    <h3 className="font-serif-display text-2xl md:text-3xl lg:text-[2.2rem] leading-[1.1] tracking-tight text-ivory font-medium">
                                        {c.title}
                                    </h3>
                                    <p className="mt-4 text-ivory/70 text-base md:text-[1.05rem] leading-relaxed font-light">
                                        {c.body}
                                    </p>
                                    <div className="mt-6 h-px w-12 bg-lime/40" />
                                </div>

                            </motion.li>
                        );
                    })}
                </ol>
            </div>
        </section>
    );
}